/* eslint-disable */
import React, { useState, useEffect } from 'react';
import './salesAndDeliveryWiseData.scss';
import '../WeeklyClosureReport/clouserReport.scss';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import DataGridComponent from '../../../../Components/DataGrid/dataGrid';
import ButtonComponent from '../../../../Components/Button/button';
import { Menu, MenuItem } from '@mui/material';
import ArrowDropDown from '../../../../Assets/images/svg/drop-down-arrow.svg';
import excelIcon from '../../../../Assets/images/svg/excel.svg';
import excelhoverIcon from '../../../../Assets/images/svg/excel_hover.svg';
import DateRangeEditor from '../../../../Components/CustomDateRange/dateRangeInput';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';

import {
  getSalesMemberWiseReport,
  getDeliveryManagerWiseReport,
} from '../../../../Api/request/misReports';
import { getSalesMemberWiseReportExcel, getDeliveryManagerWiseReportExcel } from '../../../../Api/request/reports';

import UseAxios from '../../../../Api/http/http';
import { defaultDateRanges } from '../defaultDate';
import {
  SALES_DELIVERY_WISE_REPORT,
  DATE_NOTE_HEADING,
  DATE_NOTE_DESCRIPTION,
} from '../../../../Constants/strings';

const SalesAndDeliveryWiseData = ({ reportType, dateRange: globalDateRange, dealType }) => {
  const isSale = reportType === 'sale';
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLaptopView, setIsLaptopView] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExcelHovered, setIsExcelHovered] = useState(false);
  const location = useLocation();
  const { snackbarOpen } = useSnackbar();

  const initialDateRange = defaultDateRanges.find(range => range.label === 'This Week') || {};
  const [dateRange, setDateRange] = useState({
    startDate: initialDateRange.startDate,
    endDate: initialDateRange.endDate,
    label: initialDateRange.label,
  });

  const formatDate = (date) => (date ? format(date, 'yyyy-MM-dd') : '');

  const fetchReport = async (startDate, endDate, dealType) => {
    try {
      const apiFn = isSale ? getSalesMemberWiseReport : getDeliveryManagerWiseReport;
      const response = await UseAxios(apiFn(formatDate(startDate), formatDate(endDate), dealType));
      if (response?.status === 200) {
        const details = response.data?.data?.deliveryOverviewDetails || [];
        const updatedDetails = details.map((item) => ({
          ...item,
          conversionRate: Number(item.conversionRate),
          id: uuidv4(),
        }));

        const totalsRow = {
          id: 'Total',
          [isSale ? 'salesMember' : 'deliveryManager']: 'Total',
          totalPositions: response.data?.data?.totalPositionCount,
          pendingPosition: response.data?.data?.totalPendingPositionCount,
          closedPositions: response.data?.data?.totalClosedPositionCount,
          lostPosition: response.data?.data?.totalLostPositionCount,
          conversionRate: response.data?.data?.totalConversionRate,
          revenueLoss: response.data?.data?.totalRevenueLoss,
        };

        const updatedDataWithTotals = updatedDetails.length > 0
          ? [...updatedDetails, totalsRow]
          : [];
        setPageSize(updatedDataWithTotals.length || 20);
        setRows(updatedDataWithTotals);
      } else {
        setRows([]);
        setPageSize(20);
      }
    } catch (error) {
      console.error('Fetch Report Error:', error);
      setRows([]);
      setPageSize(20);
    }
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    if (newDateRange.startDate && newDateRange.endDate) {
      fetchReport(newDateRange.startDate, newDateRange.endDate, dealType);
    }
  };

  useEffect(() => {
    if (initialDateRange?.label) handleDateRangeChange(initialDateRange);
  }, []);

  useEffect(() => {
    if (globalDateRange?.startDate && globalDateRange?.endDate) {
      setDateRange(globalDateRange);
      fetchReport(globalDateRange.startDate, globalDateRange.endDate, dealType);
    }
  }, [globalDateRange, dealType]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
      setIsLaptopView(window.innerWidth >= 1024 && window.innerWidth <= 1200);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [location]);

  const getColWidth = (m, l, d) => (isMobileView ? m : isLaptopView ? l : d);

  const columns = [
    {
      field: isSale ? 'salesMember' : 'deliveryManager',
      headerName: isSale ? 'Sales Manager' : 'Delivery Manager',
      width: getColWidth(168, 200, 130),
      minWidth: 168,
      flex: 0.1,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: 'sales-delivery-cell',
    },
    ...Object.entries(SALES_DELIVERY_WISE_REPORT.SALES_DELIVERY_FIELD)
      .filter(([key, field]) => {
        // Filter out unnecessary fields
        const allowedFields = [
          'DEAL_TYPE',
          'GEOGRAPHY',
          'NO_OF_POSITIONS',
          'CLOSED_POSITIONS',
          'PENDING_POSITIONS',
          'LOST_POSITIONS',
          'CONVERSION_PERCENT',
          'REVENUE_LOSS',
        ];
        return allowedFields.includes(key);
      })
      .map(([key, field]) => {
        const header = SALES_DELIVERY_WISE_REPORT.SALES_DELIVERY_HEADERNAME[key];
        const config = {
          field,
          headerName: header,
          width: getColWidth(150, 180, 150),
          minWidth: 150,
          flex: 0.1,
          sortable: false,
          disableColumnMenu: true,
          align: 'center',
          headerAlign: 'center',
        };
  
        // Add valueFormatter for specific fields
        if (field === 'conversionRate') {
          config.valueFormatter = (params) => {
            const num = Number(params);
            return isNaN(num) ? '' : num.toFixed(2);
          };
        }
  
        return config;
      }),
  ];

  const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleDownloadExcel = async () => {
    try {
      const excelFn = isSale ? getSalesMemberWiseReportExcel : getDeliveryManagerWiseReportExcel;
      await excelFn(formatDate(dateRange.startDate), formatDate(dateRange.endDate));
    } catch (error) {
      console.error('Excel Download Error:', error);
      snackbarOpen(SALES_DELIVERY_WISE_REPORT.DOWNLOAD_FAILURE_MESSAGE, SALES_DELIVERY_WISE_REPORT.ERROR_SEVERITY);
    } finally {
      handleCloseMenu();
    }
  };

  return (
    <div className="sales-delivery-report-outer-layer">
      <div className="sales-delivery-report">
        <div className="sales-delivery-top-section">
          <div className="sales-delivery-date-filter">
            <DateRangeEditor
              key={reportType === 'sale' ? `sales-member-date-range-${dateRange.startDate}-${dateRange.endDate}` : `delivery-manager-date-range-${dateRange.startDate}-${dateRange.endDate}`}
              dateRange={{
                startDate: dateRange.startDate ? new Date(dateRange.startDate) : null,
                endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
              }}
              onChange={(newDateRange) => handleDateRangeChange(newDateRange)}
              fromPlaceholder="From Date"
              toPlaceholder="To Date"
              definedRanges={defaultDateRanges}
              dateFormat='dd/mm/yyyy'
              useCurrentYear
              splitInitialCustomMonthView={true}
            />
            <div className="staff-aug-date-range-note">
              <strong className='staff-aug-date-heading'>{DATE_NOTE_HEADING}</strong> {DATE_NOTE_DESCRIPTION}
            </div>
          </div>
          <div>
            <ButtonComponent
              label={SALES_DELIVERY_WISE_REPORT.DOWNLOAD}
              variant="text"
              btnCss="download-skill-btn"
              onClick={handleDownloadClick}
              endIcon={<img src={ArrowDropDown} alt="Arrow Dropdown" />}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
              <MenuItem
                onClick={handleDownloadExcel}
                className="downloadskill-dropdown"
                onMouseEnter={() => setIsExcelHovered(true)}
                onMouseLeave={() => setIsExcelHovered(false)}
              >
                <img
                  style={{ marginRight: 8 }}
                  src={isExcelHovered ? excelhoverIcon : excelIcon}
                  alt="Excel Icon"
                />
                Download as Excel
              </MenuItem>
            </Menu>
          </div>
        </div>
        <div className="sales-delivery-report-grid-container">
          <DataGridComponent
            dataRows={rows}
            dataColumns={columns}
            sortFilterColumns={columns.map(col => col.field)}
            pageSize={pageSize}
            hideFooter
            isFilteredRows={rows}
            dataGridCss='sales-delivery-report-grid'
            sortingMode='server'
            isBackendPaginated={false}
            paginationMode='server'
          />
        </div>
      </div>
    </div>
  );
};

SalesAndDeliveryWiseData.propTypes = {
  reportType: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    label: PropTypes.string,
  }),
  dealType: PropTypes.arrayOf(PropTypes.string),
};

export default SalesAndDeliveryWiseData;
